<template>
  <bulk-update-wrapper
    :saving="isBulkUpdatingTags"
    title="Update Tags"
    has-changes
    :button-label="props.buttonLabel"
    @save="save"
    @dismiss="dismiss"
  >
    <vs-wrapper>
      <vs-text>I want to</vs-text>

      <v-radio-group v-model="tagUpdateOption" hide-details class="ma-0 pa-0">
        <v-row dense class="mb-1">
          <v-col class="shrink">
            <v-radio value="add" data-test="bulk-update-tag-add" />
          </v-col>
          <v-col>
            <vs-text>Add</vs-text>
          </v-col>
        </v-row>
        <v-row dense class="mb-1">
          <v-col class="shrink">
            <v-radio value="remove" data-test="bulk-update-tag-remove" />
          </v-col>
          <v-col>
            <vs-text>Remove</vs-text>
          </v-col>
        </v-row>
        <v-row dense class="mb-1">
          <v-col class="shrink">
            <v-radio value="overwrite" data-test="bulk-update-tag-overwrite" />
          </v-col>
          <v-col>
            <vs-text>Overwrite</vs-text>
          </v-col>
        </v-row>
      </v-radio-group>
      <vs-text>{{ introText }}</vs-text>
      <TagsEditor ref="tagsEditor" :initial-tags="[]" :hub-id="hubId" @error="handleError" />
    </vs-wrapper>

    <template #error-snackbar>
      <vs-snackbar :value="isSnackbarVisible" :type="snackbarType" sticky @input="clearSnackbar">
        {{ snackbarMessage || 'Failed to update tag' }}
      </vs-snackbar>
    </template>
  </bulk-update-wrapper>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { JobJson } from '@/models/types/job-json'
import { BulkUpdateResponse } from '@/models/types/bulk-update-response'
import { BulkUpdateTagsOperation, useBulkUpdateTags } from '@/api/jobs'
import BulkUpdateWrapper from '@/components/common/BulkUpdateWrapper.vue'
import TagsEditor from '@/components/common/tags/TagsEditor.vue'
import { SnackType } from '@/components/vision/SnackBarType'
import VsText from '@/components/vision/VsText.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import NewTag from '@/models/new_tag'

const tagUpdateOption = ref<'add' | 'remove' | 'overwrite'>('add')
const props = defineProps<{
  buttonLabel: string
  selectedJobs: JobJson[]
}>()
const isSnackbarVisible = ref(false)
const snackbarMessage = ref()
const snackbarType = ref(SnackType.ERROR)
const tagsEditor = ref()

const hubId = computed(() => props.selectedJobs[0].hub_id)

const emits = defineEmits<{
  (e: 'dismiss')
  (e: 'updated', { message, updatedJobs }: BulkUpdateResponse)
}>()

const { mutateAsync: bulkUpdateTags, isLoading: isBulkUpdatingTags } = useBulkUpdateTags()

const operationMapping = {
  add: BulkUpdateTagsOperation.ADD,
  remove: BulkUpdateTagsOperation.REMOVE,
  overwrite: BulkUpdateTagsOperation.OVERWRITE,
}
const save = async () => {
  try {
    await bulkUpdateTags({
      jobIds: props.selectedJobs.map((j) => j.id),
      tags:
        tagsEditor.value?.currentTags
          .filter((tag: NewTag) => tag.isEditable)
          .map((tag: NewTag) => NewTag.toObject(tag).toDisplayString) ?? [],
      operation: operationMapping[tagUpdateOption.value],
    })
    emits('updated', {
      message: 'Tag updated',
      updatedJobs: props.selectedJobs,
    })
  } catch (err) {
    isSnackbarVisible.value = true
    snackbarMessage.value = err
  }
}

const introText = computed(() => {
  if (tagUpdateOption.value === 'add') {
    return 'Add tags...'
  } else if (tagUpdateOption.value === 'remove') {
    return 'Remove existing tags...'
  } else if (tagUpdateOption.value === 'overwrite') {
    return 'Change the tags to...'
  } else {
    return 'Unknown options'
  }
})
const dismiss = () => emits('dismiss')
const clearSnackbar = () => (isSnackbarVisible.value = false)

const handleError = (err: never) => {
  isSnackbarVisible.value = true
  snackbarMessage.value = err
}
</script>
